import type { DigestSite } from "@/lib/emails";
import { cn } from "@/lib/cn";

interface ScorecardProps {
  site: DigestSite;
}

/** Per-site scorecard for the Management digest (brief §9, reference
 *  image 4). Shows open / overdue / escalations as three stacked
 *  counts with severity-driven accent. */
export function DigestScorecard({ site }: ScorecardProps) {
  const { high, medium, low } = site.severity_counts;
  const headlineSeverity =
    high > 0 ? "high" : medium > 0 ? "medium" : low > 0 ? "low" : null;
  return (
    <article
      data-testid="digest-scorecard"
      data-site={site.site}
      className="rounded border border-op-border bg-op-surface px-4 py-3"
    >
      <header className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-op-primary">{site.site}</h3>
        {headlineSeverity ? (
          <span
            className={cn(
              "rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider",
              headlineSeverity === "high" &&
                "bg-bucket-need-reply-now/20 text-bucket-need-reply-now",
              headlineSeverity === "medium" && "bg-op-amber/20 text-op-amber",
              headlineSeverity === "low" && "bg-op-accent-muted text-op-accent",
            )}
          >
            {headlineSeverity}
          </span>
        ) : null}
      </header>
      <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
        <Stat label="High" value={high} accent="text-bucket-need-reply-now" />
        <Stat label="Medium" value={medium} accent="text-op-amber" />
        <Stat label="Low" value={low} accent="text-op-accent" />
      </dl>
      {site.entries.length > 0 ? (
        <ol className="mt-3 space-y-1 border-t border-op-border pt-2">
          {site.entries.slice(0, 2).map((e) => (
            <li
              key={e.id}
              className="truncate text-[11px] text-op-secondary"
              title={e.headline}
            >
              {e.headline}
            </li>
          ))}
          {site.entries.length > 2 ? (
            <li className="text-[10px] text-op-muted">
              +{site.entries.length - 2} more
            </li>
          ) : null}
        </ol>
      ) : null}
    </article>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: string;
}) {
  return (
    <div>
      <dd
        className={cn(
          "font-mono text-xl leading-none",
          value === 0 ? "text-op-muted" : accent,
        )}
      >
        {value}
      </dd>
      <dt className="mt-1 text-[10px] uppercase tracking-wider text-op-muted">
        {label}
      </dt>
    </div>
  );
}
